// ============================================================================
// AdminProgramsPanel — grant programs + program years (Build S4, Task #25).
// ============================================================================
// Sidebar: #admin/programs (Programs & Enrollments group). Programs are
// SEEDED (award truths change via migration/ruled chores); the panel edits
// metadata, creates program years, and flips is_current. Rolling enrollment
// (OP-1): open/close dates are informational, never a gate.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertCircle, Landmark, Plus, Star, Pencil, X } from 'lucide-react';
import { api, type AdminProgram, type AdminProgramYear } from '../api';

interface Props {
  token: string;
}

// Copilot PR8: clamp BOTH fraction bounds — locale defaults may otherwise
// render more than 2 decimals for currency.
const money = (v: string | null) =>
  v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function AdminProgramsPanel({ token }: Props) {
  const [programs, setPrograms] = useState<AdminProgram[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<AdminProgram | null>(null);
  const [addingYearFor, setAddingYearFor] = useState<AdminProgram | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await api.adminPrograms(token);
      setPrograms(res.programs);
    } catch (err: any) {
      setError(err?.message ?? 'Could not load programs');
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  const flipCurrent = async (year: AdminProgramYear) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.adminProgramYearPatch(token, year.program_year_id, { is_current: true });
      await load();
    } catch (err: any) {
      setError(err?.message ?? 'Could not update program year');
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-700 bg-red-50 rounded-lg p-4">
        <AlertCircle className="w-5 h-5 shrink-0" /> {error}
      </div>
    );
  }
  if (!programs) {
    return <div className="flex items-center gap-2 text-gray-500 p-6"><Loader2 className="w-5 h-5 animate-spin" /> Loading programs…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Landmark className="w-6 h-6 text-green-700" />
        <h2 className="text-xl font-bold text-gray-900">Programs</h2>
      </div>

      {programs.map((p) => (
        <div key={p.program_id} className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100 flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono font-semibold text-green-800 bg-green-50 rounded px-1.5 py-0.5">{p.program_code}</span>
                <h3 className="font-semibold text-gray-900">{p.program_name}</h3>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{p.status}</span>
              </div>
              <div className="mt-1 text-sm text-gray-600">
                {p.funding_agency ?? '—'} · award {p.program_number ?? 'pending'} · {p.period_start ?? '—'} → {p.period_end ?? '—'}
              </div>
              <div className="mt-1 text-sm text-gray-600">
                Total {money(p.total_award)} · federal {money(p.federal_share)} · match {money(p.match_required)}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setEditing(p)}
              className="inline-flex items-center gap-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50"
            >
              <Pencil className="w-4 h-4" /> Edit metadata
            </button>
          </div>

          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-700">Program years</h4>
              <button
                type="button"
                onClick={() => setAddingYearFor(p)}
                className="inline-flex items-center gap-1 text-sm text-green-700 hover:text-green-900"
              >
                <Plus className="w-4 h-4" /> Add year
              </button>
            </div>
            {p.years.length === 0 ? (
              <p className="text-sm text-gray-500">No program years yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="py-1.5 pr-3">Year</th>
                    <th className="py-1.5 pr-3">Enrollment window</th>
                    <th className="py-1.5 pr-3">Template</th>
                    <th className="py-1.5 pr-3">Enrollments</th>
                    <th className="py-1.5 pr-3">Current</th>
                  </tr>
                </thead>
                <tbody>
                  {p.years.map((y) => (
                    <tr key={y.program_year_id} className="border-b border-gray-50">
                      <td className="py-2 pr-3 font-medium text-gray-900">{y.year_no}</td>
                      <td className="py-2 pr-3 text-gray-600">
                        {y.enrollment_open_date || y.enrollment_close_date
                          ? `${y.enrollment_open_date ?? '…'} → ${y.enrollment_close_date ?? '…'}`
                          : <span className="italic">rolling{y.enrollment_policy?.typical_waves ? ` (${y.enrollment_policy.typical_waves.join('/')})` : ''}</span>}
                      </td>
                      <td className="py-2 pr-3 text-gray-600">{y.template_agreement_version ?? '—'}</td>
                      <td className="py-2 pr-3 text-gray-600">{y.enrollment_enrolled}/{y.enrollment_total} enrolled</td>
                      <td className="py-2 pr-3">
                        {y.is_current ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> current</span>
                        ) : (
                          <button type="button" disabled={busy} onClick={() => void flipCurrent(y)} className="text-xs text-gray-500 hover:text-gray-800 underline">
                            make current
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ))}

      {editing && (
        <ProgramEditModal
          token={token}
          program={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      )}
      {addingYearFor && (
        <YearCreateModal
          token={token}
          program={addingYearFor}
          onClose={() => setAddingYearFor(null)}
          onSaved={() => { setAddingYearFor(null); void load(); }}
        />
      )}
    </div>
  );
}

function ProgramEditModal({ token, program, onClose, onSaved }: {
  token: string; program: AdminProgram; onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(program.program_name);
  const [agency, setAgency] = useState(program.funding_agency ?? '');
  const [regulation, setRegulation] = useState(program.governing_regulation_ref ?? '');
  const [status, setStatus] = useState(program.status);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await api.adminProgramPatch(token, program.program_id, {
        program_name: name,
        funding_agency: agency || null,
        governing_regulation_ref: regulation || null,
        status,
      });
      onSaved();
    } catch (e: any) {
      setErr(e?.message ?? 'Could not save');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Edit {program.program_code} metadata</h3>
          <button type="button" onClick={onClose} aria-label="Close"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <p className="text-xs text-gray-500">
          Award truths (code, number, dollars, dates) are migration-governed and not editable here.
        </p>
        {err && <p className="text-sm text-red-700">{err}</p>}
        <label className="block text-sm">
          <span className="text-gray-700">Program name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700">Funding agency</span>
          <input value={agency} onChange={(e) => setAgency(e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700">Governing regulation</span>
          <input value={regulation} onChange={(e) => setRegulation(e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700">Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 bg-white">
            <option value="active">active</option>
            <option value="closed">closed</option>
            <option value="suspended">suspended</option>
          </select>
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
          <button type="button" disabled={busy} onClick={() => void save()} className="px-4 py-1.5 text-sm font-semibold text-white bg-green-700 rounded-lg hover:bg-green-800 disabled:opacity-50">
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function YearCreateModal({ token, program, onClose, onSaved }: {
  token: string; program: AdminProgram; onClose: () => void; onSaved: () => void;
}) {
  const [yearNo, setYearNo] = useState(new Date().getFullYear());
  const [template, setTemplate] = useState('');
  const [openDate, setOpenDate] = useState('');
  const [closeDate, setCloseDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await api.adminProgramYearCreate(token, program.program_id, {
        year_no: yearNo,
        template_agreement_version: template || null,
        enrollment_open_date: openDate || null,
        enrollment_close_date: closeDate || null,
      });
      onSaved();
    } catch (e: any) {
      setErr(e?.message ?? 'Could not create year');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">New {program.program_code} program year</h3>
          <button type="button" onClick={onClose} aria-label="Close"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        {err && <p className="text-sm text-red-700">{err}</p>}
        <label className="block text-sm">
          <span className="text-gray-700">Year</span>
          <input type="number" value={yearNo} onChange={(e) => setYearNo(Number(e.target.value))} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700">Agreement template version</span>
          <input value={template} onChange={(e) => setTemplate(e.target.value)} placeholder="AMP_v1_2027" className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-sm">
            <span className="text-gray-700">Enrollment opens</span>
            <input type="date" value={openDate} onChange={(e) => setOpenDate(e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700">Enrollment closes</span>
            <input type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
          </label>
        </div>
        <p className="text-xs text-gray-500">Leave dates empty for rolling enrollment (informational only — never a gate).</p>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
          <button type="button" disabled={busy} onClick={() => void save()} className="px-4 py-1.5 text-sm font-semibold text-white bg-green-700 rounded-lg hover:bg-green-800 disabled:opacity-50">
            {busy ? 'Creating…' : 'Create year'}
          </button>
        </div>
      </div>
    </div>
  );
}
